import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { UsageEvolutionPoint } from '../types';

interface Props {
  series: UsageEvolutionPoint[] | undefined;
  loading?: boolean;
}

function toPct(ratio: number | null | undefined): number | null {
  if (ratio == null || Number.isNaN(ratio)) return null;
  return Math.round(ratio * 1000) / 10;
}

export function UsageBiasChart({ series, loading }: Props) {
  const { t } = useTranslation();

  const data = useMemo(
    () =>
      (series ?? []).map((point) => ({
        period: point.period,
        adoption: toPct(point.adoption_ratio),
        adherence: toPct(point.adherence_ratio),
        userBias: toPct(point.user_bias_ratio),
        totalBias: toPct(point.total_bias_ratio),
      })),
    [series],
  );

  if (loading && !data.length) {
    return (
      <div className="flex h-72 items-center justify-center rounded-lg border border-gray-200 bg-white text-sm text-gray-500">
        {t('adoption.chart.loading')}
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="flex h-72 items-center justify-center rounded-lg border border-gray-200 bg-white text-sm text-gray-500">
        {t('adoption.chart.empty')}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <h2 className="mb-1 text-sm font-semibold text-gray-900">{t('adoption.chart.title')}</h2>
      <p className="mb-4 text-xs text-gray-500">{t('adoption.chart.subtitle')}</p>
      <div className="h-72 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="period" tick={{ fontSize: 11 }} />
            <YAxis
              tick={{ fontSize: 11 }}
              tickFormatter={(v: number) => `${v}%`}
              domain={['auto', 'auto']}
            />
            <Tooltip
              formatter={(value: number | string, name: string) => [
                typeof value === 'number' ? `${value}%` : value,
                name,
              ]}
            />
            <Legend />
            <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="4 4" />
            <Line
              type="monotone"
              dataKey="adoption"
              name={t('adoption.chart.series.adoption')}
              stroke="#1681ba"
              strokeWidth={2}
              dot={false}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="adherence"
              name={t('adoption.chart.series.adherence')}
              stroke="#059669"
              strokeWidth={2}
              dot={false}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="userBias"
              name={t('adoption.chart.series.userBias')}
              stroke="#d97706"
              strokeWidth={2}
              dot={false}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="totalBias"
              name={t('adoption.chart.series.totalBias')}
              stroke="#dc2626"
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
